import { useCreateMeetingStepMutation } from '@gql'
import { getDefaultMeetingStep } from '@rolebase/shared/model/meeting_step'
import { useCallback, useContext } from 'react'
import useOrgMember from '../../member/hooks/useOrgMember'
import { MeetingContext } from '../contexts/MeetingContext'

// Les créations déjà en vol, partagées par TOUTES les instances du hook.
//
// 🔑 La réservation couvre la passe ENTIÈRE, posée avant la première écriture
// réseau. Réserver étape par étape ne suffit pas, et ce n'est pas une question
// de nombre d'appelants : `steps` vient d'une subscription (useMeetingState),
// donc CHAQUE insertion pousse un nouveau `steps` et relance l'effet appelant
// pendant que la boucle tourne encore. Le second passage voyait libres toutes
// les étapes que le premier n'avait pas encore atteintes, et les insérait une
// seconde fois. C'est la cécité qui produisait le doublon (#82) — la garde
// `steps.some(...)` plus bas teste un instantané Apollo qui se remplit en
// asynchrone, et deux passages la franchissent ensemble.
//
// 🔑 Un Set au niveau du module, et surtout pas un ref : il survit à un
// remontage du composant et au double montage de React en développement, deux
// cas où un ref repart vide au pire moment.
//
// Ce que la réservation ne couvre pas : elle vit dans un seul navigateur. Deux
// personnes qui ouvrent la réunion en même temps la contournent par
// construction. Ce cas-là n'a aucune conséquence : les étapes créées sont
// vides, et le `on_conflict` de la mutation ignore la seconde insertion.
//
// Le filet qui compte reste donc le `on_conflict` côté base : ce Set n'évite
// que des insertions inutiles, il ne protège de rien à lui seul.
const inFlight = new Set<string>()

// When a meeting is created, it has a stepsConfig property
// but it doesn't have step in meeting_step table.
// So we need to create a step for each stepConfig after meeting edition
//
// The circle's role is no longer fetched here: since REU-04 no step is born
// with a copy of it (the Checklist and Indicators steps read every role of the
// perimeter live instead), so the query only cost a round trip.
export default function useCreateMissingMeetingSteps() {
  const isMember = useOrgMember()
  const { meeting, circle, steps } = useContext(MeetingContext)!
  const [createMeetingStep] = useCreateMeetingStepMutation()

  return useCallback(async () => {
    if (!isMember || !meeting || !circle || !steps) return

    // Les étapes qui manquent à cet instantané
    const missingStepsConfig = meeting.stepsConfig.filter(
      (stepConfig) => !steps.some((step) => step.stepConfigId === stepConfig.id)
    )

    // Réserver TOUTES les clés libres avant la première écriture réseau, et ne
    // garder que celles que cette passe a effectivement réservées : les autres
    // appartiennent à un appel encore en vol, qui les mènera à bien.
    const claimedStepsConfig = missingStepsConfig.filter((stepConfig) => {
      const key = `${meeting.id}:${stepConfig.id}`
      if (inFlight.has(key)) return false
      inFlight.add(key)
      return true
    })

    try {
      for (const stepConfig of claimedStepsConfig) {
        await createMeetingStep({
          variables: {
            values: getDefaultMeetingStep(meeting.id, stepConfig),
          },
        })
      }
    } finally {
      // Libérées même en cas d'échec : sans ça, une erreur réseau condamnerait
      // ces étapes pour toute la session.
      for (const stepConfig of claimedStepsConfig) {
        inFlight.delete(`${meeting.id}:${stepConfig.id}`)
      }
    }
  }, [meeting, circle, steps])
}
